import { Box, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { Assessment } from '@/types';

interface QuestionNavigationProps {
  assessment: Assessment;
  currentQuestionIndex: number;
  onQuestionSelect: (questionIndex: number) => void;
  answers: (number | null)[];
  isCompleted?: boolean;
  isPreview?: boolean;
}

export const QuestionNavigation = ({
  assessment,
  currentQuestionIndex,
  onQuestionSelect,
  answers,
  isCompleted,
  isPreview,
}: QuestionNavigationProps) => {
  const completedQuestionCount = isCompleted
    ? answers.length
    : answers.filter(answer => answer !== null).length;
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const selectedQuestion = container.current?.querySelector(
      `label:nth-child(${currentQuestionIndex + 1})`,
    );
    selectedQuestion?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [currentQuestionIndex]);

  return (
    <Box
      ref={container}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexBasis: { md: 350 },
        flexShrink: 0,
        backgroundColor: 'sand150',
        borderRight: '1px solid',
        borderColor: 'grey100',
        p: { xs: 1, md: 3 },
      }}>
      <Box
        sx={[
          {
            backgroundColor: 'white',
            p: { xs: 1.5, md: 2 },
            borderRadius: 1,
            display: { xs: 'flex', md: 'block' },
            gap: 1,
            alignItems: 'center',
            mb: { xs: 1, md: 2 },
          },
          isPreview
            ? {
                filter: 'grayscale(1)',
              }
            : {},
        ]}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: { xs: 0, md: 1.5 },
            gap: 1,
          }}>
          <Typography variant="body1SemiBold">Progress</Typography>
          <Typography variant="body1">
            {completedQuestionCount} / {assessment.questions.length}
          </Typography>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            height: 12,
            borderRadius: 3,
            backgroundColor: 'secondary.medium',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              willChange: 'transform',
              top: 0,
              left: 0,
              height: '100%',
              width: `${
                (completedQuestionCount / assessment.questions.length) * 100
              }%`,
              transition: 'width 0.3s ease-in-out',
              backgroundColor: 'secondary.main',
              borderRadius: 3,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          my: { xs: 0, md: -1 },
          mx: { xs: -1, md: 0 },
          px: { xs: 1, md: 0 },
          py: { xs: 0, md: 2.5 },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          maskImage: {
            xs: 'linear-gradient(to right, transparent, #000 5%, #000 95%, transparent)',
            md: 'linear-gradient(to bottom, transparent, #000 5%, #000 95%, transparent)',
          },
          gap: 1,
          borderRadius: 1,
        }}>
        {assessment.questions.map((question, index) => (
          <Box
            component="label"
            key={question.userAssessmentQuestionId}
            onClick={() => onQuestionSelect(index)}
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              border: '1px solid transparent',
              backgroundColor:
                currentQuestionIndex === index
                  ? 'secondary.medium'
                  : 'transparent',
              borderRadius: 1,
              flexShrink: 0,
              '&:focus-within': {
                outline: '1px solid',
                outlineColor: 'secondary.main',
                outlineOffset: -1,
              },
            }}>
            <Box
              sx={{
                borderRadius: '50%',
                width: 15,
                height: 15,
                border: '2px solid',
                borderColor:
                  isCompleted || answers[index] !== null
                    ? 'secondary.main'
                    : 'grey150',
                backgroundColor:
                  isCompleted || answers[index] !== null
                    ? 'secondary.main'
                    : 'transparent',
              }}
            />
            <Typography
              variant={
                currentQuestionIndex === index ? 'body1SemiBold' : 'body1'
              }
              sx={{
                flexShrink: 0,
              }}>
              Question {index + 1}
            </Typography>
            <input
              style={{ opacity: 0, pointerEvents: 'none', position: 'fixed' }}
              type="radio"
              name="question-navigation"
              value={index}
              checked={currentQuestionIndex === index}
              onChange={() => {
                onQuestionSelect(index);
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
